import {
  Heading_4 as Heading4,
  PrimaryButton,
  useThemeContext,
} from '@huggg/react-components';
import dictionary from 'config/dictionary';
import PropTypes from 'prop-types';
import { path } from 'ramda';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  hasFulfilledVoucher,
  isFetchedHuggg,
  refetchHugggUntil,
} from 'redux/modules/huggg';
import {
  getCardStatus,
  getFormData,
  getIsRetryAfterRefusalStalled,
  getIsRetryingAfterRefusal,
  getIsStatusPollExhausted,
  isCardCreationSettled,
  retryAfterRefusal,
  startStatusPolling,
  stopStatusPolling,
} from 'redux/modules/prepaidCard';
import { getKnownProviderErrors } from 'utilities/prepaidProviderErrors';
import { CreationState } from './CreationState';
import { PrepaidFormDetails } from './PrepaidFormDetails';
import styles from './prepaidCard.module.scss';

// Waiting for the provider, the huggg becoming fetched, the poll giving up,
// or the provider having refused.
const PHASE = {
  WAITING: 'waiting',
  COMPLETING: 'completing',
  DELAYED: 'delayed',
  REFUSED: 'refused',
};

const hasCard = (state) => hasFulfilledVoucher(state) || isFetchedHuggg(state);

// The form data is only ever in redux on the tab that filled it in.
const hasFormData = (formData) => Boolean(formData?.email);

/**
 * The claim screen while a prepaid card is created. The huggg is not fetched
 * until the card exists, so the recipient stays here: the card arriving sends
 * them to the claimed view; a refusal is declined at once, with the details
 * they entered when the provider lets them try again. A reload brings the
 * form back once the refused voucher is hidden.
 */
export const PrepaidCardCreationWaitUnconnected = (props) => {
  const {
    cardId,
    segment,
    shortcode,
    cardStatus,
    formData,
    isStatusPollExhausted,
    isRetryingAfterRefusal,
    isRetryAfterRefusalStalled,
    startStatusPolling,
    stopStatusPolling,
    refetchHugggUntil,
    retryAfterRefusal,
  } = props;

  const [phase, setPhase] = useState(PHASE.WAITING);

  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const declineMessage = path(
    ['configuration', 'sangriaLayout', 'prepaidCard', 'declineMessage'],
    segment,
  );
  const { operator } = useThemeContext();
  const knownProviderErrors = useMemo(
    () => getKnownProviderErrors(shortcode, declineMessage, operator),
    [shortcode, declineMessage, operator],
  );

  useEffect(() => {
    setPhase(PHASE.WAITING);
    startStatusPolling(cardId, { settleWhen: isCardCreationSettled });

    return () => stopStatusPolling();
  }, [cardId, startStatusPolling, stopStatusPolling]);

  const creationFailure = cardStatus?.creationFailure ?? null;
  const isCardCreated = Boolean(
    cardStatus && !creationFailure && cardStatus.status !== 'pending-creation',
  );

  useEffect(() => {
    if (phase !== PHASE.WAITING) {
      return;
    }

    if (isCardCreated) {
      // The redirect to the claimed view fires once the huggg reads as fetched.
      setPhase(PHASE.COMPLETING);
      refetchHugggUntil(hasCard, {
        shouldStop: () => unmountedRef.current,
      }).then((satisfied) => {
        if (!unmountedRef.current) {
          setPhase(satisfied ? PHASE.COMPLETING : PHASE.DELAYED);
        }
      });
      return;
    }

    if (creationFailure) {
      setPhase(PHASE.REFUSED);
      return;
    }

    if (isStatusPollExhausted) {
      setPhase(PHASE.DELAYED);
    }
  }, [
    phase,
    isCardCreated,
    creationFailure,
    isStatusPollExhausted,
    refetchHugggUntil,
  ]);

  // The retry runs in redux: the huggg reload it starts remounts this screen,
  // so its progress and outcome are read back from the store. Once the
  // refused voucher is gone the wait unmounts and the form renders, prefilled
  // from the details still held in redux.
  const renderRefusal = () => (
    <>
      <CreationState
        creationFailure={creationFailure}
        knownProviderErrors={knownProviderErrors}
      />
      {creationFailure.resubmittable && (
        <div className={styles.formSubmitButton}>
          <PrimaryButton
            data-test-id="prepaid-card-retry-after-refusal"
            label={dictionary.prepaidCards.labels.editDetails}
            onClick={() => retryAfterRefusal()}
            disabled={isRetryingAfterRefusal || isRetryAfterRefusalStalled}
            loading={isRetryingAfterRefusal}
          />
          {isRetryAfterRefusalStalled && (
            <Heading4>
              {dictionary.prepaidCards.creation.retryUnavailableMessage}
            </Heading4>
          )}
        </div>
      )}
    </>
  );

  // Laid out as the confirmation step lays out a declined request, not as
  // the centred waiting copy.
  if (
    phase === PHASE.REFUSED &&
    creationFailure.resubmittable &&
    hasFormData(formData)
  ) {
    return (
      <div data-test-id="prepaid-card-creation-wait">
        <div className={styles.confirmationBlock}>
          <PrepaidFormDetails formData={formData} />
        </div>
        <div className={styles.detailsAlert}>{renderRefusal()}</div>
      </div>
    );
  }

  const renderPhase = () => {
    switch (phase) {
      case PHASE.DELAYED:
        return (
          <Heading4>{dictionary.prepaidCards.creation.delayedMessage}</Heading4>
        );

      case PHASE.REFUSED:
        return renderRefusal();

      default:
        return <CreationState knownProviderErrors={knownProviderErrors} />;
    }
  };

  return (
    <div
      data-test-id="prepaid-card-creation-wait"
      className={styles.creationWait}
    >
      {renderPhase()}
    </div>
  );
};

PrepaidCardCreationWaitUnconnected.propTypes = {
  cardId: PropTypes.string.isRequired,
  segment: PropTypes.object,
  shortcode: PropTypes.string,
  cardStatus: PropTypes.object,
  formData: PropTypes.object,
  isStatusPollExhausted: PropTypes.bool,
  isRetryingAfterRefusal: PropTypes.bool,
  isRetryAfterRefusalStalled: PropTypes.bool,
  startStatusPolling: PropTypes.func.isRequired,
  stopStatusPolling: PropTypes.func.isRequired,
  refetchHugggUntil: PropTypes.func.isRequired,
  retryAfterRefusal: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  cardStatus: getCardStatus(state),
  formData: getFormData(state),
  isStatusPollExhausted: getIsStatusPollExhausted(state),
  isRetryingAfterRefusal: getIsRetryingAfterRefusal(state),
  isRetryAfterRefusalStalled: getIsRetryAfterRefusalStalled(state),
  shortcode: path(['huggg', 'huggg', 'shortcode'], state),
});

export const PrepaidCardCreationWait = connect(mapStateToProps, {
  startStatusPolling,
  stopStatusPolling,
  refetchHugggUntil,
  retryAfterRefusal,
})(PrepaidCardCreationWaitUnconnected);
